import {
  Button,
  Form,
  Icons,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  useSelect,
} from '@pankod/refine-antd'
import { ModalProps, FormProps } from '@pankod/refine-antd'
import React from 'react'

import { TAdmin } from '../types'

type CertificateCategoryModalFormProps = {
  modalProps: ModalProps
  formProps: FormProps
  onClose: () => void
  mode?: 'create' | 'edit'
}

const CertificateCategoryModalForm = ({
  modalProps,
  formProps,
  onClose,
  mode = 'create',
}: CertificateCategoryModalFormProps) => {
  const { selectProps: actorSelectProps } = useSelect<TAdmin>({
    resource: 'admins',
    dataProviderName: 'lms',
    optionLabel: 'email',
    optionValue: 'userId',
    filters: [{ field: 'role', operator: 'eq', value: 'otorisasi-sertifikat' }],
    onSearch: (value) => [{ field: 'email', operator: 'eq', value }],
    debounce: 300,
    defaultValueQueryOptions: { enabled: false },
    queryOptions: { enabled: !!modalProps.visible },
  })
  const selectedActors = formProps.form?.getFieldValue('actors') || []
  const isEditMode = mode === 'edit'

  return (
    <Modal
      {...modalProps}
      title={isEditMode ? 'Ubah Kategori' : 'Tambah Kategori'}
      okText={isEditMode ? 'Simpan Perubahan' : 'Tambah Kategori'}
      cancelText="Batal"
      onCancel={() => {
        onClose()
        formProps.form?.resetFields()
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        validateMessages={{ required: '${label} harus diisi.' }}
      >
        <Form.Item
          label="Nama Kategori"
          name="name"
          rules={[{ required: true }]}
        >
          <Input autoComplete="off" />
        </Form.Item>

        {!isEditMode && (
          <Form.Item
            label="Status"
            name="isActive"
            initialValue={true}
            valuePropName="checked"
            required
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Tidak Aktif" />
          </Form.Item>
        )}

        <Form.Item label="Aktor" required>
          <Form.List
            name="actors"
            initialValue={[{ userId: undefined, role: undefined, order: 1 }]}
            rules={[
              {
                validator: async (_, actors) => {
                  if (!actors || actors.length < 1) {
                    return Promise.reject(
                      new Error('Minimal 1 aktor dibutuhkan.'),
                    )
                  }

                  const selectedUserIds = actors
                    .map((actor?: { userId?: string }) => actor?.userId)
                    .filter(Boolean)

                  if (
                    new Set(selectedUserIds).size !== selectedUserIds.length
                  ) {
                    return Promise.reject(
                      new Error('Email aktor tidak boleh duplikat.'),
                    )
                  }

                  return Promise.resolve()
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...rest }) => {
                  const currentUserId = selectedActors?.[name]?.userId
                  const selectedUserIds = selectedActors
                    .map((actor?: { userId?: string }) => actor?.userId)
                    .filter(
                      (userId?: string) =>
                        Boolean(userId) && userId !== currentUserId,
                    )

                  return (
                    <Space
                      key={key}
                      className="flex gap-2 w-full !items-start mb-2 last-of-type:mb-3"
                    >
                      <Form.Item
                        {...rest}
                        name={[name, 'userId']}
                        rules={[
                          { required: true, message: 'Email harus diisi.' },
                          {
                            validator: async (_, value) => {
                              if (value && selectedUserIds.includes(value)) {
                                return Promise.reject(
                                  new Error(
                                    'Email aktor tidak boleh duplikat.',
                                  ),
                                )
                              }

                              return Promise.resolve()
                            },
                          },
                        ]}
                        help="Email aktor"
                      >
                        <Select
                          {...actorSelectProps}
                          options={actorSelectProps.options?.map((option) => ({
                            ...option,
                            disabled:
                              option.value !== currentUserId &&
                              selectedUserIds.includes(option.value as string),
                          }))}
                          showSearch
                          placeholder="Cari email aktor"
                          filterOption={false}
                          className="!w-[280px]"
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'role']}
                        rules={[
                          { required: true, message: 'Role harus diisi.' },
                        ]}
                        help="Role"
                      >
                        <Select
                          options={[
                            { label: 'Approver', value: 'approver' },
                            { label: 'Signer', value: 'signer' },
                          ]}
                          placeholder="Pilih role"
                          className="!w-[120px]"
                          showSearch={false}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'jabatan']}
                        rules={[
                          { required: true, message: 'Jabatan harus diisi.' },
                        ]}
                        help="Jabatan"
                      >
                        <Input
                          placeholder="Jabatan"
                          autoComplete="off"
                          className="!w-[160px]"
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'unitKerja']}
                        rules={[
                          {
                            required: true,
                            message: 'Unit Kerja harus diisi.',
                          },
                        ]}
                        help="Unit Kerja"
                      >
                        <Input
                          placeholder="Unit Kerja"
                          autoComplete="off"
                          className="!w-[160px]"
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'order']}
                        rules={[
                          { required: true, message: 'Urutan harus diisi.' },
                        ]}
                        help="Urutan"
                      >
                        <InputNumber
                          min={1}
                          placeholder="Urutan"
                          className="!w-[80px]"
                        />
                      </Form.Item>

                      {fields.length > 1 && (
                        <Button
                          danger
                          type="text"
                          icon={<Icons.MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          className="mt-1"
                        />
                      )}
                    </Space>
                  )
                })}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      userId: undefined,
                      role: undefined,
                      order: fields.length + 1,
                    })
                  }
                  icon={<Icons.PlusOutlined />}
                  block
                >
                  Tambah Aktor
                </Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default CertificateCategoryModalForm
